<template>
    <div v-if="ouvert" class="scor-overlay" @click.self="fermer">
        <div class="scor-modal" role="dialog" aria-modal="true">
            <!-- Entete + badge de statut -->
            <div class="scor-entete">
                <div>
                    <h3 class="scor-titre">{{ titre }}</h3>
                    <div class="scor-sous-titre">FSE n° {{ noFse }}</div>
                </div>
                <div class="scor-entete-droite">
                    <span class="badge" :class="badgeClasse">{{ badgeTexte }}</span>
                    <button class="scor-fermer" @click="fermer" title="Fermer">×</button>
                </div>
            </div>

            <!-- Message statut (raison manque / copie papier degrade) -->
            <div v-if="messageStatut" class="scor-message" :class="{ 'scor-message--warn': statutManquant }">
                {{ messageStatut }}
            </div>

            <!-- Liste des pieces jointes -->
            <div class="scor-section">
                <div class="scor-section-titre">Pièces jointes</div>
                <p v-if="!pieces.length && !chargement" class="scor-vide">Aucune pièce jointe.</p>
                <p v-if="chargement" class="scor-vide">Chargement…</p>
                <table v-if="pieces.length" class="scor-table">
                    <thead>
                        <tr><th>Nom</th><th>Nature</th><th>Date</th><th>Taille</th><th></th></tr>
                    </thead>
                    <tbody>
                        <tr v-for="p in pieces" :key="p.Nom">
                            <td>{{ p.Nom }}</td>
                            <td>{{ p.Nature }}</td>
                            <td>{{ formaterDate(p.Date) }}</td>
                            <td>{{ p.TailleKo }} Ko</td>
                            <td class="scor-actions-cellule">
                                <button class="btn-ligne" @click="apercuPiece(p.Nom)" title="Aperçu">Aperçu</button>
                                <button class="btn-ligne btn-suppr" @click="supprimerPiece(p.Nom)" title="Supprimer">Suppr.</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Ajout : depuis la biblio patient -->
            <div class="scor-section" v-if="identPatient > 0">
                <div class="scor-section-titre">Depuis les ordonnances du patient</div>
                <div v-if="!ordonnances.length && !chargementBiblio" class="scor-vide">
                    Aucune ordonnance en bibliothèque pour ce patient.
                </div>
                <p v-if="chargementBiblio" class="scor-vide">Chargement…</p>
                <table v-if="ordonnances.length" class="scor-table">
                    <tbody>
                        <tr v-for="o in ordonnances" :key="o.Nom">
                            <td>{{ o.Nom }}</td>
                            <td>{{ formaterDate(o.Date) }}</td>
                            <td>{{ o.TailleKo }} Ko</td>
                            <td class="scor-actions-cellule">
                                <button class="btn-ligne" @click="apercuOrdonnance(o.Nom)" title="Aperçu">Aperçu</button>
                                <button class="btn-ligne btn-signer" @click="attacher(o.Nom)" title="Joindre à la FSE">Joindre</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Ajout : numeriser / importer -->
            <div class="scor-section">
                <div class="scor-section-titre">Numériser / Importer une pièce</div>
                <div class="scor-import">
                    <input ref="fichier" type="file" accept="application/pdf,image/*" @change="onFichier" />
                    <label class="scor-champ">
                        Nature :
                        <select v-model="nature">
                            <option v-for="n in natures" :key="n.code" :value="n.code">{{ n.libelle }}</option>
                        </select>
                    </label>
                    <label v-if="identPatient > 0" class="scor-check">
                        <input type="checkbox" v-model="ajoutBiblio" />
                        Ajouter aussi à la bibliothèque du patient
                    </label>
                    <button class="close-button scor-btn-ajout" :disabled="!fichierChoisi || envoiEnCours" @click="ajouterPiece">
                        {{ envoiEnCours ? 'Envoi…' : 'Ajouter' }}
                    </button>
                </div>
                <p class="scor-astuce">Envoyer de préférence un PDF (numérisation plus fiable).</p>
            </div>
        </div>
    </div>
</template>

<script>
    import { defineComponent } from 'vue';
    import Scor from '../script/Scor.js';

    export default defineComponent({
        name: 'DialogPiecesScor',
        props: {
            ouvert: { type: Boolean, default: false },
            noFse: { type: [Number, String], default: 0 },
            identPatient: { type: [Number, String], default: 0 },
            titre: { type: String, default: 'Pièces jointes SCOR' },
        },
        emits: ['update:ouvert', 'close', 'statut-change'],
        data() {
            return {
                pieces: [],
                ordonnances: [],
                statut: null,
                chargement: false,
                chargementBiblio: false,
                envoiEnCours: false,
                nature: 'ORDN',
                ajoutBiblio: false,
                fichierChoisi: null,
                natures: [
                    { code: 'ORDN', libelle: 'Ordonnance / prescription' },
                    { code: 'FSNC', libelle: 'Feuille de soins papier' },
                    { code: 'FSDE', libelle: 'Feuille de soins dégradée' },
                    { code: 'DSIP', libelle: 'Démarche de soins infirmiers' },
                    { code: 'AUTP', libelle: 'Autre pièce justificative' },
                    { code: 'PAPIER', libelle: 'Envoi papier (non numérisé)' },
                ],
            };
        },
        computed: {
            statutManquant() {
                return this.statut && (this.statut.code === 1 || this.statut.code === 2);
            },
            badgeTexte() {
                if (!this.statut) return 'SCOR';
                switch (this.statut.code) {
                    case 3: return 'Complet';
                    case 2: return 'Partiel';
                    case 1: return 'Manquant';
                    case 0: return 'Non requis';
                    default: return 'Erreur';
                }
            },
            badgeClasse() {
                if (!this.statut) return 'badge--neutre';
                switch (this.statut.code) {
                    case 3: return 'badge--ok';
                    case 2:
                    case 1: return 'badge--warn';
                    case 0: return 'badge--neutre';
                    default: return 'badge--erreur';
                }
            },
            messageStatut() {
                if (!this.statut) return '';
                const parts = [];
                if (this.statutManquant && this.statut.raisonManque) {
                    parts.push(`Pièce manquante : ${this.libelleNature(this.statut.raisonManque)}.`);
                }
                if (this.statut.copiePapierDegradeRequise) {
                    parts.push('FSE dégradée : joindre la copie papier de la feuille de soins.');
                }
                return parts.join(' ');
            },
        },
        watch: {
            ouvert(v) {
                if (v) this.charger();
            },
        },
        methods: {
            libelleNature(code) {
                const n = this.natures.find((x) => x.code === code);
                return n ? n.libelle : (code || '');
            },
            formaterDate(d) {
                if (!d) return '';
                const dt = new Date(d);
                return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString('fr-FR');
            },
            appliquerStatut(reponse) {
                const s = reponse && (reponse.statut ?? reponse.Statut);
                this.statut = Scor.normaliserStatut(s);
                this.$emit('statut-change', this.statut);
            },
            async charger() {
                if (!this.noFse) return;
                this.chargement = true;
                try {
                    const r = await Scor.listerPieces(this.noFse);
                    this.pieces = (r && (r.pieces ?? r.Pieces)) || [];
                    this.appliquerStatut(r);
                } catch (e) {
                    console.error('SCOR - liste pieces :', e);
                } finally {
                    this.chargement = false;
                }
                if (this.identPatient > 0) this.chargerBiblio();
            },
            async chargerBiblio() {
                this.chargementBiblio = true;
                try {
                    const r = await Scor.listerOrdonnances(this.identPatient);
                    this.ordonnances = (r && (r.ordonnances ?? r.Ordonnances)) || [];
                } catch (e) {
                    console.error('SCOR - biblio ordonnances :', e);
                } finally {
                    this.chargementBiblio = false;
                }
            },
            onFichier(e) {
                this.fichierChoisi = (e.target.files && e.target.files[0]) || null;
            },
            async ajouterPiece() {
                if (!this.fichierChoisi) return;
                this.envoiEnCours = true;
                try {
                    const b64 = await Scor.fichierEnBase64(this.fichierChoisi);
                    const nomFichier = this.fichierChoisi.name;
                    const r = await Scor.ajouterPiece(this.noFse, {
                        FichierBase64: b64, NomFichier: nomFichier, Nature: this.nature,
                    });
                    if (r && (r.success ?? r.Success)) {
                        this.appliquerStatut(r);
                        if (this.ajoutBiblio && this.identPatient > 0) {
                            await Scor.ajouterOrdonnance(this.identPatient, { FichierBase64: b64, NomFichier: nomFichier });
                        }
                        this.reinitImport();
                        await this.charger();
                    }
                } catch (e) {
                    console.error('SCOR - ajout piece :', e);
                } finally {
                    this.envoiEnCours = false;
                }
            },
            async attacher(nom) {
                try {
                    const r = await Scor.attacherDepuisPatient(this.noFse, { IdentPatient: Number(this.identPatient), Nom: nom });
                    if (r && (r.success ?? r.Success)) {
                        this.appliquerStatut(r);
                        await this.charger();
                    }
                } catch (e) {
                    console.error('SCOR - attache depuis patient :', e);
                }
            },
            async apercuPiece(nom) {
                try {
                    const r = await Scor.contenuPiece(this.noFse, nom);
                    const c = r && (r.contenuBase64 ?? r.ContenuBase64);
                    if (c) Scor.apercuBase64(c, nom);
                } catch (e) {
                    console.error('SCOR - apercu piece :', e);
                }
            },
            async apercuOrdonnance(nom) {
                try {
                    const r = await Scor.contenuOrdonnance(this.identPatient, nom);
                    const c = r && (r.contenuBase64 ?? r.ContenuBase64);
                    if (c) Scor.apercuBase64(c, nom);
                } catch (e) {
                    console.error('SCOR - apercu ordonnance :', e);
                }
            },
            async supprimerPiece(nom) {
                if (!confirm(`Supprimer la pièce « ${nom} » ?`)) return;
                try {
                    const r = await Scor.supprimerPiece(this.noFse, nom);
                    if (r && (r.success ?? r.Success)) {
                        this.appliquerStatut(r);
                        await this.charger();
                    }
                } catch (e) {
                    console.error('SCOR - suppression piece :', e);
                }
            },
            reinitImport() {
                this.fichierChoisi = null;
                this.ajoutBiblio = false;
                this.nature = 'ORDN';
                if (this.$refs.fichier) this.$refs.fichier.value = '';
            },
            fermer() {
                this.reinitImport();
                this.$emit('update:ouvert', false);
                this.$emit('close');
            },
        },
    });
</script>

<style scoped>
    .scor-overlay {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.45);
        display: flex;
        align-items: flex-start;
        justify-content: center;
        z-index: 3000;
        overflow-y: auto;
        padding: 24px 12px;
    }
    .scor-modal {
        background: #fff;
        border-radius: 10px;
        width: 100%;
        max-width: 720px;
        box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
        padding: 16px 18px 20px;
    }
    .scor-entete {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
        border-bottom: 1px solid #e5e9f0;
        padding-bottom: 10px;
    }
    .scor-titre {
        margin: 0;
        color: #004AAD;
        font-size: 18px;
    }
    .scor-sous-titre {
        color: #6b7280;
        font-size: 12px;
        margin-top: 2px;
    }
    .scor-entete-droite {
        display: flex;
        align-items: center;
        gap: 10px;
    }
    .scor-fermer {
        border: none;
        background: transparent;
        font-size: 24px;
        line-height: 1;
        cursor: pointer;
        color: #6b7280;
    }
    .scor-fermer:hover { color: #111; }

    .scor-message {
        margin: 10px 0 0;
        padding: 8px 10px;
        border-radius: 6px;
        font-size: 13px;
        background: #f3f4f6;
        color: #374151;
    }
    .scor-message--warn {
        background: #ffedd5;
        color: #9a3412;
    }

    .scor-section {
        margin-top: 16px;
    }
    .scor-section-titre {
        font-weight: 600;
        color: #1f2937;
        margin-bottom: 6px;
        font-size: 14px;
    }
    .scor-vide {
        color: #6b7280;
        font-size: 13px;
        margin: 4px 0;
    }
    .scor-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
    }
    .scor-table th {
        text-align: left;
        color: #6b7280;
        font-weight: 600;
        border-bottom: 1px solid #e5e9f0;
        padding: 4px 6px;
    }
    .scor-table td {
        padding: 5px 6px;
        border-bottom: 1px solid #f1f3f7;
        vertical-align: middle;
    }
    .scor-actions-cellule {
        white-space: nowrap;
        text-align: right;
    }
    .btn-ligne {
        border: 1px solid #b9c4d4;
        background: #eef3fa;
        color: #004AAD;
        border-radius: 4px;
        padding: 2px 8px;
        margin: 0 2px;
        font-size: 12px;
        cursor: pointer;
    }
    .btn-ligne:hover { background: #d9e6f5; }
    .btn-ligne.btn-signer {
        background: #e3f5e3;
        color: #1f7a1f;
        border-color: #9fd39f;
    }
    .btn-ligne.btn-suppr {
        background: #fdecec;
        color: #b32020;
        border-color: #e6a3a3;
    }

    .scor-import {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px;
    }
    .scor-champ, .scor-check {
        font-size: 13px;
        color: #374151;
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }
    .scor-champ select {
        padding: 3px 6px;
        border: 1px solid #b9c4d4;
        border-radius: 4px;
    }
    .scor-btn-ajout {
        margin: 0;
        padding: 6px 16px;
        font-size: 14px;
    }
    .scor-btn-ajout:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
    .scor-astuce {
        color: #6b7280;
        font-size: 12px;
        margin: 6px 0 0;
    }

    .badge {
        font-size: 12px;
        padding: 2px 10px;
        border-radius: 999px;
        font-weight: 700;
        white-space: nowrap;
    }
    .badge--ok { background: #dcfce7; color: #166534; }
    .badge--erreur { background: #fee2e2; color: #b91c1c; }
    .badge--warn { background: #ffedd5; color: #9a3412; }
    .badge--neutre { background: #f3f4f6; color: #374151; }

    .close-button {
        border: none;
        border-radius: 4px;
        background-color: #004AAD;
        color: white;
        font-weight: bold;
        cursor: pointer;
        transition: background-color 0.3s ease;
    }
    .close-button:hover { background-color: #0060d6; }
</style>
